import { createFileRoute } from "@tanstack/react-router";
import { marketingHead } from "@/lib/marketing/seo";
import { breadcrumbs } from "@/lib/marketing/ppl-schema";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";

export const Route = createFileRoute("/v2/privacy")({
  component: PrivacyPage,
  head: () => ({
    ...marketingHead({
      title: "Privacy policy: the People AI browser extension | PPL AI",
      description:
        "What the People AI Chrome extension handles, where it goes, and what it never does: it works only on Walmart, Sam's Club and Sysco, only in tabs it opens, and buys nothing without a person's approval.",
      path: "/v2/privacy",
      imageAlt: "PPL AI",
      schema: [breadcrumbs({ name: "Privacy", path: "/privacy" })],
    }),
  }),
});

const SECTIONS: Array<{ heading: string; points: string[] }> = [
  {
    heading: "What the extension handles",
    points: [
      "Pairing: the address of the People AI server and a pairing token for the community it is paired to. These are kept in the extension's local storage on that computer until it is unpaired.",
      "Store sign-ins: when a store asks for a sign-in, the extension receives the sign-in the community saved in People AI and types it into that store's own sign-in page. The extension does not keep it.",
      "Store page content: product names, prices, sizes and availability from the Walmart, Sam's Club and Sysco pages Nancy opens. These are sent to the community's People AI account to build the cart and the order.",
    ],
  },
  {
    heading: "What it does not do",
    points: [
      "It works only on walmart.com, samsclub.com and sysco.com, and only in tabs it opens itself.",
      "It does not read your other tabs, your browsing history or any other site.",
      "It does not buy anything. A person in the community approves every cart in People AI.",
      "We do not sell this data or use it for advertising, and we share it only with the service providers who run People AI for us.",
    ],
  },
  {
    heading: "Where the data goes",
    points: [
      "To the community's own People AI account, over an encrypted connection. It is kept as part of the community's order records under the terms of the community's People AI agreement.",
    ],
  },
  {
    heading: "Your choices",
    points: [
      "Unpair the extension from its popup or from Nancy's settings in People AI, or remove it from Chrome, and it stops at once.",
      "To ask about or delete data, use Get help inside the People AI app, or email nikhil@ppl-ai.com.",
    ],
  },
  {
    heading: "Changes",
    points: ["We will update this page when the extension changes what it handles."],
  },
];

function PrivacyPage() {
  return (
    <MarketingShell>
      <div className="mx-auto max-w-3xl px-6 py-16 sm:py-24">
        <p className="text-muted-foreground text-sm">Last updated 28 September 2026</p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-balance sm:text-4xl">
          Privacy policy
        </h1>
        <p className="text-muted-foreground mt-4 text-lg">
          PPL AI makes software for assisted living communities. This policy covers the People AI
          Chrome extension, which lets our purchasing assistant, Nancy, shop a community's stores in
          the browser it is installed in.
        </p>
        <div className="mt-10 space-y-10">
          {SECTIONS.map((section) => (
            <section key={section.heading}>
              <h2 className="text-xl font-semibold">{section.heading}</h2>
              <ul className="mt-3 list-disc space-y-2 pl-5">
                {section.points.map((point) => (
                  <li key={point} className="leading-relaxed">
                    {point}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </MarketingShell>
  );
}
